// utils/pushNotifications.js
// Browser Web Push helpers for the customer client. Pure browser + API glue;
// never requests permission without an explicit user action (caller-driven).

import api from '../api/api'

const SW_URL = '/service-worker.js'

export const isPushSupported = () =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window

export const getPushPermission = () =>
    (typeof Notification !== 'undefined' ? Notification.permission : 'denied')

// Must be called from a user gesture (e.g. button click).
export const requestPushPermission = async () => {
    if (!isPushSupported()) return 'denied'
    return Notification.requestPermission()
}

export const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) return null
    try {
        const existing = await navigator.serviceWorker.getRegistration(SW_URL)
        if (existing) return existing
        return await navigator.serviceWorker.register(SW_URL, { scope: '/' })
    } catch (e) {
        return null
    }
}

export const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const raw = window.atob(base64)
    const output = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i)
    return output
}

const getPublicKey = async () => {
    try {
        const { data } = await api.get('/push/public-key')
        if (!data?.enabled || !data?.publicKey) return null
        return data.publicKey
    } catch (e) {
        return null
    }
}

// Subscribe this device and persist it on the backend. Assumes permission is
// already granted. Returns true on success.
export const subscribeToPush = async (publicVapidKey) => {
    if (!isPushSupported()) return false
    const key = publicVapidKey || await getPublicKey()
    if (!key) return false

    const registration = await registerServiceWorker()
    if (!registration) return false
    await navigator.serviceWorker.ready

    let subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(key)
        })
    }

    const json = subscription.toJSON()
    try {
        await api.post('/push/subscribe', {
            endpoint: subscription.endpoint,
            keys: json.keys,
            deviceName: navigator.platform || ''
        })
        return true
    } catch (e) {
        return false
    }
}

export const unsubscribeFromPush = async () => {
    if (!('serviceWorker' in navigator)) return false
    try {
        const registration = await navigator.serviceWorker.getRegistration(SW_URL)
        const subscription = registration && await registration.pushManager.getSubscription()
        if (subscription) {
            await api.post('/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {})
            await subscription.unsubscribe()
        }
        return true
    } catch (e) {
        return false
    }
}

// Silent path: if permission is already granted, make sure the subscription is
// saved (e.g. after re-login or on a new device). Does NOT prompt.
export const ensurePushSubscription = async () => {
    if (!isPushSupported() || getPushPermission() !== 'granted') return false
    return subscribeToPush()
}

export const getPushStatus = async () => {
    try {
        const { data } = await api.get('/push/status')
        return data
    } catch (e) {
        return { subscribed: false, enabled: false }
    }
}
